import { useIntl } from "react-intl";
import { Typography, Box, Grid } from "@mui/material";
import Messages from "../../../../../localization/Messages";
import ChartComponent from "../../ChartComponent/ChartComponent";
import { DashboardStats } from "../../../../../models/stats";
import { formatNumber } from "../../../../../helpers/numberDisplayHelper";
import Tooltip from "@mui/material/Tooltip";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

export type BookingAnalyticsProps = {
    dashboardStats: DashboardStats;
};

export const BookingAnalytics = ({ dashboardStats }: BookingAnalyticsProps) => {
    const intl = useIntl();
    const { bookingPerStatus, daysPerType } = dashboardStats;

    const totalBookings =
        bookingPerStatus.length > 0
            ? bookingPerStatus.reduce((acc, stat) => acc + stat.count, 0)
            : 0;

    const totalDays =
        daysPerType.length > 0 ? daysPerType[0].arrivalDay + daysPerType[0].layDay : 0;

    const oneDayBookings =
        daysPerType.length > 0
            ? totalBookings -
              (daysPerType[0].days2 + daysPerType[0].days3 + daysPerType[0].days4plus)
            : 0;

    const totalBookingsWithArrivalDay = daysPerType.length > 0 ? daysPerType[0].arrivalDay : 0;

    const lengthOfStayAvg =
        totalBookingsWithArrivalDay && totalDays ? totalDays / totalBookingsWithArrivalDay : 0;

    return (
        <Box className="bookings-analytics-container shadow" my={2} mx={2.5} padding={2.5}>
            <Typography variant="h5">
                {intl.formatMessage(Messages.bookingsConfirmedChanged)}{" "}
                <Tooltip title={intl.formatMessage(Messages.onlyInvoicedConfirmedChangedTooltip)}>
                    <HelpOutlineIcon fontSize="small" color="action" />
                </Tooltip>
            </Typography>
            <Grid container spacing={0.5}>
                <Grid item xs={12} md={6} lg={6}>
                    <ChartComponent
                        data={
                            daysPerType.length > 0
                                ? [
                                      {
                                          label: intl.formatMessage(Messages.arrivalDays),
                                          value: daysPerType[0].arrivalDay,
                                      },
                                      {
                                          label: intl.formatMessage(Messages.layDays),
                                          value: daysPerType[0].layDay,
                                      },
                                  ]
                                : []
                        }
                        description={intl.formatMessage(Messages.numberOfDaysByType)}
                        outerStats={[
                            `${intl.formatMessage(Messages.totalDays)} = ${formatNumber(
                                totalDays,
                            )}`,
                        ]}
                        formatter={value =>
                            `${formatNumber(value)} / ${((value / totalDays) * 100).toFixed(2)}%`
                        }
                    />
                </Grid>
                <Grid item xs={12} md={6} lg={6}>
                    <ChartComponent
                        data={
                            daysPerType.length > 0
                                ? [
                                      {
                                          label: `1 ${intl.formatMessage(Messages.day)}`,
                                          value: oneDayBookings,
                                      },
                                      {
                                          label: `2 ${intl.formatMessage(Messages.days)}`,
                                          value: daysPerType[0].days2,
                                      },
                                      {
                                          label: `3 ${intl.formatMessage(Messages.days)}`,
                                          value: daysPerType[0].days3,
                                      },
                                      {
                                          label: `>=4 ${intl.formatMessage(Messages.days)}`,
                                          value: daysPerType[0].days4plus,
                                      },
                                  ]
                                : []
                        }
                        description={intl.formatMessage(Messages.numberOfbookingsByLengthOfStay)}
                        outerStats={[
                            `${intl.formatMessage(
                                Messages.averageLengthOfStay,
                            )} = ${lengthOfStayAvg.toFixed(1)} ${intl.formatMessage(
                                Messages.days,
                            )}`,
                        ]}
                        formatter={value =>
                            `${formatNumber(value)} / ${((value / totalDays) * 100).toFixed(2)}%`
                        }
                    />
                </Grid>
            </Grid>
        </Box>
    );
};

export default BookingAnalytics;
